import { ref } from "vue";
import { Order, OrderItem } from "@/types";

export type PreviewBlob = { url: string; mimeType?: string };

const BROWSER_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp", "image/bmp", "image/svg+xml", "image/avif"]);
const BROWSER_IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "avif", "pdf"]);

/** A PSD is `image/*` too, and the browser cannot draw it. */
export function isBrowserPreviewable(mimeType?: string, name?: string): boolean {
  const mime = mimeType?.toLowerCase() || "";
  if (mime === "application/pdf" || BROWSER_IMAGE_TYPES.has(mime)) return true;
  const extension = name?.split(".").pop()?.toLowerCase();
  return !!extension && BROWSER_IMAGE_EXTENSIONS.has(extension) && !mime.includes("photoshop");
}

const byOrderItemId = new Map<number, PreviewBlob>();
const byFileUuid = new Map<string, PreviewBlob>();
/** Bumped on write so thumbnails re-read the maps. The row can render before the blob is stored. */
const revision = ref(0);

type PreviewCarrier = {
  preview?: { id?: number; metadata?: { Preview?: string; signedPreviewUrl?: string } | null };
  otherPreviews?: Array<{ id?: number; metadata?: { Preview?: string; signedPreviewUrl?: string } | null }>;
};

/** `pending` means a preview row exists but nre-service has not uploaded the image yet. */
export function serverPreviewState(file?: PreviewCarrier | null): "missing" | "pending" | "ready" {
  const preview = file?.preview || file?.otherPreviews?.[0];
  if (!preview?.id) return "missing";
  if (preview.metadata?.Preview === "Done") return "ready";
  if (preview.metadata?.signedPreviewUrl) return "pending";
  return "ready";
}

/** A list refetch started before the webhook must not replace a preview that already finished uploading. */
export function preserveReadyPreviews<T extends { id?: number; currentFile?: PreviewCarrier | null }>(previous: T[] | undefined, next: T[]): T[] {
  if (!previous?.length) return next;
  const readyById = new Map<number, T>();
  for (const item of previous) {
    if (item.id != null && serverPreviewState(item.currentFile) === "ready") readyById.set(Number(item.id), item);
  }
  if (!readyById.size) return next;
  return next.map((item) => {
    if (item.id == null) return item;
    const ready = readyById.get(Number(item.id));
    if (!ready || serverPreviewState(item.currentFile) === "ready") return item;
    return { ...item, currentFile: ready.currentFile };
  });
}

export function previewFileId(file?: PreviewCarrier | null): number | undefined {
  const id = file?.preview?.id || file?.otherPreviews?.[0]?.id;
  return typeof id === "number" && id > 0 ? id : undefined;
}

export function rememberPreviewBlob(key: { orderItemId?: number | null; fileUuid?: string | null }, blob: PreviewBlob) {
  if (!blob.url) return;
  const orderItemId = key.orderItemId != null ? Number(key.orderItemId) : undefined;
  if (orderItemId != null && orderItemId > 0) byOrderItemId.set(orderItemId, blob);
  if (key.fileUuid) byFileUuid.set(key.fileUuid, blob);
  revision.value++;
}

export function lookupPreviewBlob(orderItemId?: number | null, fileUuid?: string | null): PreviewBlob | undefined {
  revision.value;
  const id = orderItemId != null ? Number(orderItemId) : undefined;
  if (id != null && id > 0 && byOrderItemId.has(id)) return byOrderItemId.get(id);
  if (fileUuid && byFileUuid.has(fileUuid)) return byFileUuid.get(fileUuid);
  return undefined;
}

export function forgetPreviewBlob(orderItemId?: number | null, fileUuid?: string | null) {
  const id = orderItemId != null ? Number(orderItemId) : undefined;
  if (id != null) byOrderItemId.delete(id);
  if (fileUuid) byFileUuid.delete(fileUuid);
  revision.value++;
}

function ensureObjectUrl(uploadedFile: any): PreviewBlob | undefined {
  if (!uploadedFile) return undefined;
  if (!uploadedFile.objectURL && typeof Blob !== "undefined" && uploadedFile instanceof Blob) {
    (uploadedFile as Blob & { objectURL?: string }).objectURL = URL.createObjectURL(uploadedFile);
  }
  if (!uploadedFile.objectURL) return undefined;
  const name = uploadedFile.name as string | undefined;
  const extension = name?.split(".").pop()?.toLowerCase();
  const mimeFromName =
    extension === "png" ? "image/png" : extension === "jpg" || extension === "jpeg" ? "image/jpeg" : extension === "gif" ? "image/gif" : extension === "webp" ? "image/webp" : extension === "pdf" ? "application/pdf" : undefined;
  return { url: uploadedFile.objectURL, mimeType: uploadedFile.type || mimeFromName };
}

/** Keep local blobs addressable by the saved order item after the table refetches without `uploadedFile`. */
export function rememberOrderPreviewBlobs(savedOrder: Order, localItems: OrderItem[]) {
  for (const local of localItems) {
    const blob = ensureObjectUrl(local.uploadedFile);
    const saved = savedOrder.orderItems?.find((item) => item.line != null && item.line === local.line);
    if (!blob || !saved?.id) continue;
    rememberPreviewBlob({ orderItemId: saved.id, fileUuid: saved.currentFile?.uuid }, blob);
  }
}
